import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import moment from 'moment';
import { combineLatest, forkJoin, Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';

import {
  AnalyticsApi,
  Device,
  DeviceIds,
  DeviceRoles,
  FeatureNames,
  Gateway,
  HemsPayload,
  HemsProperties,
  HemsType,
  HemsUnit,
  hemsUnitToCommonUnitMapping,
  RawHemsHistoricalData,
  TimeResolution,
  ViError,
} from '@vi-iot/client';

import { FilterType } from '../../+installations/installation/energy-historical-data-modal/energy-filters-header/energy-filters.data';
import { Unit } from '../../shared/common/units';
import {
  DashboardTypes,
  InstallationDetailsStore,
  isLagCascadeV2Device,
} from '../installation-details';

import { TranslateService } from '@ngx-translate/core';
import { ViToastService } from '@vi-iot/ui/index';
import { DatesService } from 'src/app/shared/services/dates.service';
import { AnalyticsStore } from '../analytics';
import { DeviceTypes } from '../gateway';
import { EnergyHistoricalDataActions } from './energy-historical-data.actions';
import {
  filterFeaturesByFilterType,
  HemsFlowType,
  powerProperties,
  thermalProperties,
} from './energy-historical-data.helpers';
import { EnergyHistoricalDataMappers } from './energy-historical-data.mappers';
import { HemsHistoricalData, SavedSelectedFeature } from './energy-historical-data.model';
import { selectedChartKey } from './energy-historical-data.state';
import { EnergyHistoricalDataStore } from './energy-historical-data.store';
import { InstallationFeaturesStore } from '../installation-features';
import { DevicesStore } from '../devices';
import { DevicesServiceModels } from '../devices/devices.service.models';
import { DevicesService } from '../devices/devices.service';

@Injectable()
export class EnergyHistoricalDataEffects {
  monthFlag = true;

  constructor(
    private actions$: Actions,
    private energyHistoricalDataStore: EnergyHistoricalDataStore,
    private analyticalApi: AnalyticsApi,
    private installationDetailsStore: InstallationDetailsStore,
    private analyticsStore: AnalyticsStore,
    private datesService: DatesService,
    private toastService: ViToastService,
    private translateService: TranslateService,
    private installationFeaturesStore: InstallationFeaturesStore,
    private devicesStore: DevicesStore,
    private devicesService: DevicesService
  ) {}

  loadChartData$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(EnergyHistoricalDataActions.loadChartData),
      switchMap((action) =>
        combineLatest([
          combineLatest([
            of(action),
            this.energyHistoricalDataStore.filterType$.pipe(take(1)),
            this.energyHistoricalDataStore.filterDate$.pipe(take(1)),
            this.installationDetailsStore.getHemsGateway$.pipe(take(1)),
            this.devicesStore.isPhotovoltaicOBNG$.pipe(
              switchMap(
                (isOBNG): Observable<DevicesServiceModels.WallboxDeviceWithBusAddres[]> => {
                  if (isOBNG) {
                    return this.devicesService.wallboxDevicesWithBusAddress$;
                  } else {
                    return of([]);
                  }
                }
              )
            ),
            this.devicesStore.dryContactGeneric$.pipe(take(1)),
          ]),
          combineLatest([
            this.installationDetailsStore.installationTimezone$.pipe(take(1)),
            this.analyticsStore.selectEarlierstDateFeatureForHemsAnalysisTab$.pipe(take(1)),
            this.energyHistoricalDataStore.hemsAnalysisSelectedTab$,
            this.installationFeaturesStore.activeHEMSSubscription$,
            this.devicesStore.getDevicesByRole(DeviceRoles.SystemControllerSCU).pipe(
              take(1),
              map((devices) => devices[0])
            ),
          ]),
        ])
      ),
      switchMap(
        ([
          [
            _action,
            filterType,
            filterDate,
            gatewayProperties,
            busAdresses,
            dryContactGenericDevices,
          ],
          [
            installationTimeZone,
            selectEarlierstDateFeatureForHemsAnalysisTab,
            hemsAnalysisSelectedTab,
            activeHEMSSubscription,
            scuDevice,
          ],
        ]) => {
          const payloadBase: HemsPayload = this.createHemsPayload(
            filterDate.toISOString(),
            filterType,
            gatewayProperties,
            installationTimeZone,
            selectEarlierstDateFeatureForHemsAnalysisTab
          );

          let mappedPowerProperties = structuredClone(powerProperties);

          const filteredBusAddresses = busAdresses.filter(
            (device) => device.busAddress !== undefined
          );
          if (filteredBusAddresses.length) {
            mappedPowerProperties = mappedPowerProperties.filter(
              (property) => property !== FeatureNames.emsEnergyConsumptionVcs
            );

            const busAddressValues = filteredBusAddresses.map((el) => el.busAddress);

            busAddressValues.forEach((busAddress) => {
              mappedPowerProperties.push(
                FeatureNames.emsEnergyConsumptionVcsWithIndex(busAddress) as HemsProperties
              );
            });
          }

          dryContactGenericDevices
            .map((device) => device.id.split('-')[1])
            .filter((index) => !!index)
            .forEach((index) => {
              mappedPowerProperties.push(
                FeatureNames.emsEnergyConsumptionOtherWithIndex(index) as HemsProperties
              );
            });

          const powerPropertiesPayload = filterFeaturesByFilterType(
            mappedPowerProperties,
            filterType
          );
          const thermalPropertiesPayload = filterFeaturesByFilterType(
            thermalProperties,
            filterType
          );

          let powerData$: Observable<{ data: RawHemsHistoricalData }>;

          if (activeHEMSSubscription === HemsType.Full) {
            powerData$ = this.analyticalApi.getHemsPowerData({
              ...payloadBase,
              properties: powerPropertiesPayload,
            });
          } else {
            powerData$ = of({
              data: {
                gateway_id: gatewayProperties?.serial,
                device_id: payloadBase.device_id,
                resolution: payloadBase.resolution,
                timeframe: payloadBase.end_datetime + ' - ' + payloadBase.start_datetime,
                summary_properties: {},
                timeseries_properties: {},
                data: {
                  summary: {},
                  timeseries: [],
                },
              },
            }) as Observable<{ data: RawHemsHistoricalData }>;
          }

          // for thermal we don't want to pass in any device_id
          // when device_id is missing then big data will implicitly treat it as deviceId=0
          // which is correct because thermal tab is only for heatpump
          const thermalPayload = {
            ...payloadBase,
            device_id: scuDevice?.id || undefined,
          } as HemsPayload;

          const thermalData$ = this.analyticalApi.getHemsThermalData({
            ...thermalPayload,
            properties: thermalPropertiesPayload,
          });

          return forkJoin([powerData$, thermalData$]).pipe(
            map(([powerData, thermalData]) => {
              const timeseriesProperties = this.mapHemsUnitsToCommonUnit({
                ...powerData.data.timeseries_properties,
                ...thermalData.data.timeseries_properties,
              });

              const summaryProperties = this.mapHemsUnitsToCommonUnit({
                ...powerData.data.summary_properties,
                ...thermalData.data.summary_properties,
              });

              const mergedData: HemsHistoricalData = {
                // same value in each request
                device_id: powerData.data.device_id,
                gateway_id: powerData.data.gateway_id,
                resolution: powerData.data.resolution,
                timeframe: powerData.data.timeframe,
                // merged data
                timeseries_properties: timeseriesProperties,
                properties: {
                  ...powerData.data.properties,
                  ...thermalData.data.properties,
                },
                summary_properties: summaryProperties,
                data: {
                  summary: {
                    ...powerData.data.data.summary,
                    ...thermalData.data.data.summary,
                  },
                  timeseries:
                    hemsAnalysisSelectedTab === HemsFlowType.Power
                      ? powerData.data.data.timeseries
                      : thermalData.data.data.timeseries,
                },
              };

              return EnergyHistoricalDataActions.loadChartDataSuccess({
                chartData: mergedData,
              });
            }),
            catchError((error: ViError) => {
              this.toastService.error({
                message: this.translateService.instant(
                  '+INSTALLATIONS.INSTALLATION.HEMS.ANALYSIS_CHART.ERROR_NOTICE'
                ),
              });

              return of(EnergyHistoricalDataActions.loadChartDataError({ error }));
            }),
            takeUntil(
              this.installationDetailsStore.getSelectedDashboardTypeHelper$.pipe(
                filter((dashboardType) => dashboardType !== DashboardTypes.HEMS)
              )
            )
          );
        }
      ),
      catchError((error: ViError) => {
        this.toastService.error({
          message: this.translateService.instant(
            '+INSTALLATIONS.INSTALLATION.HEMS.ANALYSIS_CHART.ERROR_NOTICE'
          ),
        });

        return of(EnergyHistoricalDataActions.loadChartDataError({ error }));
      })
    );
  });

  loadLegacyHPChartData$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(EnergyHistoricalDataActions.loadLegacyHPChartData),
      map((action) => {
        const currentDate = moment().format('YYYY-MM-DDThh:mm');
        const payload: HemsPayload = {
          properties: [action.feature],
          gateway_id: action.gatewaySerial,
          device_id: '0',
          end_datetime: currentDate,
        };

        let timeRange: HemsPayload[] = [];

        if (action.fetchOnlyDayValues) {
          timeRange.push({
            ...payload,
            resolution: '1d',
            start_datetime: moment().subtract(8, 'days').format('YYYY-MM-DDThh:mm'),
          });
        } else {
          timeRange.push({
            ...payload,
            resolution: '1W',
            start_datetime: moment().subtract(12, 'weeks').format('YYYY-MM-DDThh:mm'),
          });
          timeRange.push({
            ...payload,
            resolution: '1M',
            start_datetime: moment().subtract(12, 'months').format('YYYY-MM-DDThh:mm'),
          });
          timeRange.push({
            ...payload,
            resolution: '1Y',
            start_datetime: moment().subtract(2, 'years').format('YYYY-MM-DDThh:mm'),
          });
        }

        return { timeRange, action };
      }),
      switchMap(({ timeRange, action }) =>
        forkJoin(
          timeRange.map((element) => {
            return this.analyticalApi.getHemsThermalData(element).pipe(
              catchError(() => {
                return of(null);
              })
            );
          })
        ).pipe(
          filter((response) => {
            if (response.length === 1) {
              return response[0] !== null;
            }

            return true;
          }),
          map((response) =>
            EnergyHistoricalDataMappers.mapLegacyHPResponse(response, action.feature)
          ),
          map((legacyChartData) => {
            return EnergyHistoricalDataActions.loadLegacyHPChartDataSuccess({
              legacyChartData,
            });
          })
        )
      ),
      catchError(() => {
        this.toastService.error({
          message: this.translateService.instant(
            '+INSTALLATIONS.INSTALLATION.HEMS.ANALYSIS_LEGACY_HP_CHART_ERROR'
          ),
        });

        return of(EnergyHistoricalDataActions.loadLegacyHPChartDataError());
      })
    );
  });

  saveSelectedFeatureToStorage$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(EnergyHistoricalDataActions.saveSelectedFeatureToStorage),
        switchMap((action) =>
          combineLatest([
            of(action),
            this.energyHistoricalDataStore.savedSelectedFeatures$.pipe(take(1)),
          ])
        ),
        tap(([action, selectedFeatures]) => {
          const objToSave = selectedFeatures;
          if (action.isThermalFlow) {
            objToSave[action.installationId].heatingSelectedFeatures = action.savedSelectedFeatures;
          } else {
            objToSave[action.installationId].electricitySelectedFeatures =
              action.savedSelectedFeatures;
          }
          sessionStorage.setItem(selectedChartKey, JSON.stringify(objToSave));
        })
      );
    },
    { dispatch: false }
  );

  loadSelectedFeatureFromStorage$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(EnergyHistoricalDataActions.loadSelectedFeatureFromStorage.attempt),
      map(() => {
        const selectedFeaturesFromStorage: SavedSelectedFeature = JSON.parse(
          sessionStorage.getItem(selectedChartKey)
        );

        return EnergyHistoricalDataActions.loadSelectedFeatureFromStorage.success({
          savedSelectedFeature: selectedFeaturesFromStorage,
        });
      }),
      catchError((error) =>
        of(EnergyHistoricalDataActions.loadSelectedFeatureFromStorage.error({ error }))
      )
    );
  });

  private createHemsPayload(
    filterDateInput: string,
    filterType: FilterType,
    gatewayProperties: Gateway,
    installationTimeZone: string = this.datesService.getDayjsWithTimezone()?.tz?.guess(),
    earliestDate: string
  ): HemsPayload {
    const dayjs = this.datesService.getDayjsWithTimezone();
    let payload: HemsPayload;
    let resolution: TimeResolution;
    let startDate: string;
    let endDate: string;
    const timeZoneFormat = 'YYYY-MM-DDTHH:mm:ss.SSSZZ';
    const nonTimeZoneFormat = 'YYYY-MM-DDTHH:mm:ss.SSS';
    const registeredAt = dayjs(earliestDate).format(timeZoneFormat);
    const filterDate = dayjs(filterDateInput).tz(installationTimeZone, true);

    switch (filterType) {
      case FilterType.Day:
        startDate = filterDate.startOf('day').format(nonTimeZoneFormat);
        endDate = filterDate.add(1, 'day').format(nonTimeZoneFormat);
        resolution = '15m';
        break;
      case FilterType.Month:
        startDate = filterDate.startOf('month').format(nonTimeZoneFormat);
        endDate = filterDate.endOf('month').format(nonTimeZoneFormat);
        resolution = '1d';
        break;
      case FilterType.Year:
        startDate = filterDate.add(1, 'day').startOf('year').format(nonTimeZoneFormat);
        endDate = filterDate.endOf('year').format(nonTimeZoneFormat);
        resolution = '1M';
        break;
      case FilterType.Total:
        startDate = filterDate.add(1, 'day').startOf('year').format(nonTimeZoneFormat);
        endDate = dayjs().format(nonTimeZoneFormat);
        resolution = '1Y';
        break;
      default:
        break;
    }

    const optionalBoilerSerialForVitocharge = this.getCascadeVitochargeSerialForHemsPayload(
      gatewayProperties
    );

    const SCUSerial = this.getSCUSerial(gatewayProperties);

    payload = {
      gateway_id: gatewayProperties?.serial,
      device_id: SCUSerial ?? optionalBoilerSerialForVitocharge,
      start_datetime: startDate || registeredAt,
      end_datetime: endDate,
      resolution,
    };

    return payload;
  }

  private mapHemsUnitsToCommonUnit(
    properties: { [key in HemsProperties]: HemsUnit }
  ): { [key in HemsProperties]: Unit } {
    const propertiesNames = Object.keys(properties);
    const mappedProperties: { [key in HemsProperties]: Unit } = (<unknown>{}) as {
      [key in HemsProperties]: Unit;
    };

    propertiesNames.forEach((entry: HemsProperties) => {
      mappedProperties[entry] = hemsUnitToCommonUnitMapping[properties[entry]];
    });

    return mappedProperties;
  }

  private getCascadeVitochargeSerialForHemsPayload(gatewayProperties: Gateway): string {
    let consolidatorElectric: Device;
    let lagDevice: Device;

    gatewayProperties.devices.forEach((device) => {
      if (device.deviceType === DeviceTypes.ElectricityStorage) {
        if (device.roles.includes(DeviceRoles.ConsolidatorElectric)) {
          consolidatorElectric = device;
        }

        if (isLagCascadeV2Device(device)) {
          lagDevice = device;
        }
      }
    });

    return consolidatorElectric?.id ?? lagDevice?.boilerSerial ?? DeviceIds.Main;
  }

  private getSCUSerial(gatewayProperties: Gateway): string {
    let serial: string;

    gatewayProperties?.devices.forEach((device) => {
      if (device.deviceType === DeviceTypes.SystemController) {
        serial = device.boilerSerial;
      }
    });

    return serial;
  }
}
